import { doesFocusableExist } from '@noriginmedia/norigin-spatial-navigation';
import { instantScrollBehaviorOnFocusKey } from '../../stripes/_utils/types';
import { uiStore } from '../_stores/store-ui';

export const isSheetDialogOpen = () =>
  uiStore.toggles.recordingDialog || uiStore.toggles.providersVersionsDialog;

const findClosestExistingFocusKey = (
  previousFocusKey: string,
  openCollections: boolean,
  defaultFocusKey: string
): string => {
  if (doesFocusableExist(previousFocusKey)) {
    return previousFocusKey;
  }

  if (openCollections) {
    const collectionMatch = previousFocusKey.match(/^(\d+)-collection$/);

    if (collectionMatch) {
      const currentIndex = Number(collectionMatch[1]);

      for (let index = currentIndex - 1; index >= 0; index -= 1) {
        const candidate = `${index}-collection`;
        if (doesFocusableExist(candidate)) {
          return candidate;
        }
      }
    }

    return defaultFocusKey;
  }

  // Handle showAll item - if it no longer exists, fall back to last content item in that stripe
  const showAllMatch = previousFocusKey.match(/^showAll-(\d+)$/);

  if (showAllMatch) {
    const stripeIndex = showAllMatch[1];

    // Try to find the last content item in this stripe
    // Start from a reasonable upper bound and work backwards
    for (let index = 100; index >= 0; index -= 1) {
      const candidate = `${index}-${stripeIndex}`;
      if (doesFocusableExist(candidate)) {
        return candidate;
      }
    }

    return defaultFocusKey;
  }

  const stripeMatch = previousFocusKey.match(/^(\d+)-(\d+)$/);

  if (stripeMatch) {
    const currentIndex = Number(stripeMatch[1]);
    const stripeIndex = stripeMatch[2];

    for (let index = currentIndex - 1; index >= 0; index -= 1) {
      const candidate = `${index}-${stripeIndex}`;
      if (doesFocusableExist(candidate)) {
        return candidate;
      }
    }
  }

  return defaultFocusKey;
};

/**
 * Handles setting the focus key to the previous focus key or a default focus key.
 * If the previous focus key exists, it sets the focus to it. Otherwise, it sets the focus to a default key.
 *
 * @param {FocusKeyPrev} focusKeyPrev - The previous focus key.
 * @param {boolean} openCollections - Whether collections are open.
 * @param {function} handleSetDialogFocusKey - Function to set the dialog focus key.
 * @param {function} setFocusKeyPrev - Function to set the previous focus key.
 */
export const handleFocusKeyPrev = (
  focusKeyPrev: any,
  openCollections: boolean,
  handleSetDialogFocusKey: (key: string) => void,
  setFocusKeyPrev: (value: any) => void
) => {
  if (focusKeyPrev?.focusKeyPrev) {
    sessionStorage.setItem(instantScrollBehaviorOnFocusKey, 'true'); // set the initial system set scroll behavior to instant
    const defaultFocusKey = openCollections ? '0-collection' : '0-0'; // focuskey of the first element in either collections or stripe page
    const toFocus = findClosestExistingFocusKey(
      focusKeyPrev.focusKeyPrev,
      openCollections,
      defaultFocusKey
    );

    handleSetDialogFocusKey(toFocus);

    setFocusKeyPrev(null);
  }
};
